import React from 'react';
import ArrowRightIcon from '@atlaskit/icon/core/migration/arrow-right';
import SettingsIcon from '@atlaskit/icon/core/migration/settings';
import { expVal } from '@atlassian/jira-feature-experiments';
import { SITE_ADMIN_SETTINGS_ITEM_HREFS } from '@atlassian/jira-atlassian-navigation-global-settings-home/src/constants.tsx';
import {
	COMMAND_PALETTE_ATLASSIAN_ADMIN_PRIORITY,
	COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY,
	COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY,
	COMMAND_PALETTE_SECTIONS,
	COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY,
} from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { useTransformNavigationCommands } from '@atlassian/jira-command-palette-common/src/common/utils/transform-navigation-commands/index.tsx';
import { useCommandKeywords } from '@atlassian/jira-command-palette-common/src/controllers/use-command-keywords/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useAtlassianPersonalSettingsLink } from '@atlassian/jira-navigation-apps-common/src/utils/index.tsx';
import { getGeneralPersonalSettingsPath } from '@atlassian/jira-personal-settings-paths/src/index.tsx';
import { useNavigationPlansVisibility } from '@atlassian/jira-plans-item-visibility/src/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { boardsDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/boardsDirectoryRoute.tsx';
import { dashboardsDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/dashboardsDirectoryRoute.tsx';
import { filtersDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/filtersDirectoryRoute.tsx';
import { plansDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/plansDirectoryRoute.tsx';
import { projectsDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/projectsDirectoryRoute.tsx';
import { generalPersonalSettingsRoute } from '@atlassian/jira-router-routes-personal-settings-routes/src/generalPersonalSettingsRoute.tsx';
import type { User } from '@atlassian/jira-shared-types/src/rest/jira/user.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { issueNavigatorGlobalRoute } from '@atlassian/jira-router-routes-issue-navigator-routes/src/issueNavigatorGlobalRoute.tsx';
import { useIssueNavigatorLink } from '../../../controllers/use-issue-navigator-link/index.tsx';
import {
	GO_TO_ISSUES_COMMAND_ID,
	GO_TO_PERSONAL_SETTINGS_COMMAND_ID,
	VIEW_MY_OPEN_ISSUES_COMMAND_ID,
	VIEW_ISSUES_REPORTED_BY_ME_COMMAND_ID,
	VIEW_ALL_BOARDS_COMMAND_ID,
	VIEW_ALL_DASHBOARDS_COMMAND_ID,
	VIEW_ALL_FILTERS_COMMAND_ID,
	VIEW_ALL_PLANS_COMMAND_ID,
	VIEW_ALL_PROJECTS_COMMAND_ID,
	GO_TO_ACCOUNT_SETTINGS_COMMAND_ID,
	SITE_ADMIN_USER_MANAGEMENT,
	SITE_ADMIN_BILLING,
} from './constants.tsx';
import messages from './messages.tsx';

export const useNavigationCommands = () => {
	const { formatMessage } = useIntl();
	const { getKeywords } = useCommandKeywords();
	const { issueNavigatorLink } = useIssueNavigatorLink();

	const { hideAndTransformNavigationCommands } = useTransformNavigationCommands();

	const { data } = useCurrentUser();
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const email = (data.user as User)?.emailAddress;
	const accountSettingsLink = useAtlassianPersonalSettingsLink(email || undefined);

	const [isPlansVisible] = useNavigationPlansVisibility();

	const { isSiteAdmin } = useTenantContext();

	const commands = hideAndTransformNavigationCommands([
		...(fg('compal_jifjam_navigation_new_filters')
			? [
					{
						command: {
							id: VIEW_MY_OPEN_ISSUES_COMMAND_ID,
							name: formatMessage(messages.viewMyOpenIssues),
							keywords: getKeywords('viewMyOpenIssuesSynonyms'),
							section: COMMAND_PALETTE_SECTIONS.siteNavigation,
							priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.viewMyIssues,
							components: {
								LeftIcon: () => <ArrowRightIcon spacing="spacious" label="" />,
							},
							analytics: {
								action: 'viewMyOpenIssues',
							},
						},
						path: `${issueNavigatorGlobalRoute.path}?filter=-1`,
					},
					{
						command: {
							id: VIEW_ISSUES_REPORTED_BY_ME_COMMAND_ID,
							name: formatMessage(messages.viewIssuesReportedByMe),
							keywords: getKeywords('viewIssuesReportedByMeSynonyms'),
							section: COMMAND_PALETTE_SECTIONS.siteNavigation,
							priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.viewMyIssues,
							components: {
								LeftIcon: () => <ArrowRightIcon spacing="spacious" label="" />,
							},
							analytics: {
								action: 'viewIssuesReportedByMe',
							},
						},
						path: `${issueNavigatorGlobalRoute.path}?filter=-2`,
					},
				]
			: []),
		{
			command: {
				id: VIEW_ALL_PROJECTS_COMMAND_ID,
				name: formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.viewAllProjectsGalaxia
						: messages.viewAllProjects,
				),
				keywords: getKeywords('viewAllProjectsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.siteNavigation,
				priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToProjects,
				components: {
					LeftIcon: () => (
						<ArrowRightIcon
							spacing="spacious"
							label={
								fg('compal_jifjam_navigation_new_filters')
									? ''
									: formatMessage(
											expVal('project-terminology-refresh', 'isEnabled', false)
												? messages.viewAllProjectsGalaxia
												: messages.viewAllProjects,
										)
							}
						/>
					),
				},
				analytics: {
					action: 'viewAllProjects',
				},
			},
			path: projectsDirectoryRoute,
		},
		{
			command: {
				id: VIEW_ALL_FILTERS_COMMAND_ID,
				name: formatMessage(messages.viewAllFilters),
				keywords: getKeywords('viewAllFiltersSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.siteNavigation,
				priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToFilters,
				components: {
					LeftIcon: () => (
						<ArrowRightIcon
							spacing="spacious"
							label={
								fg('compal_jifjam_navigation_new_filters')
									? ''
									: formatMessage(messages.viewAllFilters)
							}
						/>
					),
				},
				analytics: {
					action: 'viewAllFilters',
				},
			},
			path: filtersDirectoryRoute,
		},
		{
			command: {
				id: VIEW_ALL_DASHBOARDS_COMMAND_ID,
				name: formatMessage(messages.viewAllDashboards),
				keywords: getKeywords('viewAllDashboardsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.siteNavigation,
				priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToDashboards,
				components: {
					LeftIcon: () => (
						<ArrowRightIcon
							spacing="spacious"
							label={
								fg('compal_jifjam_navigation_new_filters')
									? ''
									: formatMessage(messages.viewAllDashboards)
							}
						/>
					),
				},
				analytics: {
					action: 'viewAllDashboards',
				},
			},
			path: dashboardsDirectoryRoute,
		},
		{
			command: {
				id: GO_TO_PERSONAL_SETTINGS_COMMAND_ID,
				name: formatMessage(messages.goToPersonalSettings),
				keywords: getKeywords('goToPersonalSettingsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.personalSettings,
				priority: COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY.goToPersonalSettings,
				components: {
					LeftIcon: () => (
						<SettingsIcon
							spacing="spacious"
							label={
								fg('compal_jifjam_navigation_new_filters')
									? ''
									: formatMessage(messages.goToPersonalSettings)
							}
						/>
					),
				},
				analytics: {
					action: 'goToPersonalSettings',
				},
			},
			path: generalPersonalSettingsRoute,
			hideCommandPath: getGeneralPersonalSettingsPath(),
		},
		{
			command: {
				id: VIEW_ALL_BOARDS_COMMAND_ID,
				name: formatMessage(messages.viewAllBoards),
				keywords: getKeywords('viewAllBoardsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.siteNavigation,
				components: {
					LeftIcon: () => (
						<ArrowRightIcon
							spacing="spacious"
							label={
								fg('compal_jifjam_navigation_new_filters')
									? ''
									: formatMessage(messages.viewAllBoards)
							}
						/>
					),
				},
				priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToBoards,
				analytics: {
					action: 'viewAllBoards',
				},
			},
			path: boardsDirectoryRoute,
		},
		{
			command: {
				id: GO_TO_ACCOUNT_SETTINGS_COMMAND_ID,
				name: formatMessage(messages.goToAccountSettings),
				keywords: getKeywords('goToAccountSettingsSynonyms'),
				section: COMMAND_PALETTE_SECTIONS.personalSettings,
				components: {
					LeftIcon: () => (
						<SettingsIcon
							spacing="spacious"
							label={
								fg('compal_jifjam_navigation_new_filters')
									? ''
									: formatMessage(messages.goToAccountSettings)
							}
						/>
					),
				},
				priority: COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY.goToAccountSettings,
				analytics: {
					action: 'goToAccountSettings',
				},
			},
			path: accountSettingsLink,
			shouldAlwaysOpenNewTab: true,
		},
		...(issueNavigatorLink
			? [
					{
						command: {
							id: GO_TO_ISSUES_COMMAND_ID,
							name: formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.goToIssuesIssueTermRefresh
									: messages.goToIssues,
							),
							keywords: getKeywords('goToIssuesSynonyms'),
							section: COMMAND_PALETTE_SECTIONS.containerNavigation,
							components: {
								LeftIcon: () => (
									<ArrowRightIcon
										spacing="spacious"
										label={
											fg('compal_jifjam_navigation_new_filters')
												? ''
												: formatMessage(
														fg('jira-issue-terminology-refresh-m3')
															? messages.goToIssuesIssueTermRefresh
															: messages.goToIssues,
													)
										}
									/>
								),
							},
							priority: COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY.goToIssues,

							analytics: {
								action: 'goToIssues',
							},
						},
						path: issueNavigatorLink,
						hideCommandPath: projectsDirectoryRoute.path,
					},
				]
			: []),
		...(isPlansVisible
			? [
					{
						command: {
							id: VIEW_ALL_PLANS_COMMAND_ID,
							name: formatMessage(messages.viewAllPlans),
							keywords: getKeywords('viewAllPlansSynonyms'),
							section: COMMAND_PALETTE_SECTIONS.siteNavigation,
							components: {
								LeftIcon: () => (
									<ArrowRightIcon
										spacing="spacious"
										label={
											fg('compal_jifjam_navigation_new_filters')
												? ''
												: formatMessage(messages.viewAllPlans)
										}
									/>
								),
							},
							priority: COMMAND_PALETTE_SITE_NAVIGATION_PRIORITY.goToPlans,
							analytics: {
								action: 'viewAllPlans',
							},
						},
						path: plansDirectoryRoute,
					},
				]
			: []),
		...(isSiteAdmin
			? [
					{
						command: {
							id: SITE_ADMIN_USER_MANAGEMENT,
							name: formatMessage(messages.goToUserManagement),
							keywords: getKeywords('goToUserManagementSynonyms'),
							section: COMMAND_PALETTE_SECTIONS.atlassianAdmin,
							priority: COMMAND_PALETTE_ATLASSIAN_ADMIN_PRIORITY.userManagement,
							components: {
								LeftIcon: () => (
									<SettingsIcon
										spacing="spacious"
										label={
											fg('compal_jifjam_navigation_new_filters')
												? ''
												: formatMessage(messages.goToUserManagement)
										}
									/>
								),
							},
							analytics: {
								action: 'goToUserManagement',
							},
						},
						path: SITE_ADMIN_SETTINGS_ITEM_HREFS.USER_MANAGEMENT,
						shouldAlwaysOpenNewTab: true,
					},
					{
						command: {
							id: SITE_ADMIN_BILLING,
							name: formatMessage(messages.goToBilling),
							section: COMMAND_PALETTE_SECTIONS.atlassianAdmin,
							priority: COMMAND_PALETTE_ATLASSIAN_ADMIN_PRIORITY.billing,
							components: {
								LeftIcon: () => (
									<SettingsIcon
										spacing="spacious"
										label={
											fg('compal_jifjam_navigation_new_filters')
												? ''
												: formatMessage(messages.goToBilling)
										}
									/>
								),
							},
							analytics: {
								action: 'goToBilling',
							},
						},
						path: SITE_ADMIN_SETTINGS_ITEM_HREFS.BILLING,
						shouldAlwaysOpenNewTab: true,
					},
				]
			: []),
	]);

	return commands;
};
